import { graphHooks } from '@tunasong/graph-lib/react'
import { entitiesApi, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { FC } from 'react'
import { useEffect, useEffectEvent } from 'react'
import { useActionToast } from '../alert/use-action-toast.js'
import { Button } from '../primitives/button.js'
import { CircularProgress } from '../primitives/circular-progress.js'
import { Typography } from '../primitives/typography.js'
import { requestAccessCopy as copy } from './request-access-copy.js'
import styles from './request-access-button.module.css'

export interface RequestAccessButtonProps {
  entityId: string
  /** Contained on the denied page, outlined in the embed card (spec §8.2) */
  variant?: 'contained' | 'outlined'
  /**
   * `stacked` (denied page) shows the "Request sent" caption under the button. `row` (the 56px embed card) shows
   * no caption line: the card swaps its own text, and the full sentence is announced from a hidden live region
   * (spec §8.9 #3).
   */
  layout?: 'stacked' | 'row'
  /** The server says the caller can already read: the host refetches and the content is the feedback */
  onHasAccess?: () => void
}

function isShareAlertFor(alert: unknown, entityId: string): boolean {
  if (!alert || typeof alert !== 'object') {
    return false
  }
  const { type, entity } = alert as { type?: unknown; entity?: { id?: unknown } }
  return type === 'share' && entity?.id === entityId
}

const statusOf = (error: unknown) =>
  error && typeof error === 'object' ? (error as { status?: unknown }).status : undefined

/**
 * One control for every requester surface (spec §8.2). `aria-disabled`, never `disabled`, so keyboard focus stays
 * on it from sending to sent. The live region is always mounted, so the change to "Request sent" is announced.
 */
export const RequestAccessButton: FC<RequestAccessButtonProps> = props => {
  const { entityId, variant = 'contained', layout = 'stacked', onHasAccess } = props
  const { data: mine } = graphHooks.useMyAccessRequest(entityId)
  const [requestAccess, { isLoading: sending }] = graphHooks.useRequestAccess()
  const { showToast, toast } = useActionToast()
  const dispatch = useThunkDispatch()

  /** Approval arrives as a "shared with you" notification: re-check, so the item loads in place without a reload */
  const sharedHere = useSelector(state => isShareAlertFor(state.notifications.alert, entityId))
  useEffect(() => {
    if (sharedHere) {
      dispatch(entitiesApi.util.invalidateTags([{ type: 'AccessRequests', id: `mine-${entityId}` }]))
    }
  }, [sharedHere, entityId, dispatch])

  const hasAccess = mine?.state === 'has-access'
  /** An event, not a dependency: a host's inline callback must not re-fire the refetch on every render */
  const notifyHasAccess = useEffectEvent(() => onHasAccess?.())
  useEffect(() => {
    if (hasAccess) {
      notifyHasAccess()
    }
  }, [hasAccess])

  const sent = mine?.state === 'requested'
  const inert = sending || sent || hasAccess || !mine

  const handleRequest = async () => {
    if (inert) {
      return
    }
    try {
      const result = await requestAccess({ id: entityId }).unwrap()
      if (result.state === 'has-access') {
        onHasAccess?.()
      }
    } catch (error) {
      if (statusOf(error) === 429) {
        showToast({ message: copy.rateLimited })
        return
      }
      showToast({ message: copy.failed, actionLabel: copy.retry, onAction: () => void handleRequest() })
    }
  }

  const announcement = layout === 'row' ? copy.embedSent : copy.sentCaption

  return (
    <div className={layout === 'row' ? styles.row : styles.stacked}>
      <Button
        variant={variant}
        aria-disabled={inert ? 'true' : undefined}
        className={inert ? styles.inert : undefined}
        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        onClick={() => void handleRequest()}
      >
        {sent ? copy.buttonSent : copy.button}
      </Button>
      <Typography
        variant="caption"
        component="p"
        aria-live="polite"
        data-visually-hidden={layout === 'row' ? 'true' : undefined}
        className={layout === 'row' ? styles.visuallyHidden : styles.caption}
      >
        {sent ? announcement : ''}
      </Typography>
      {toast}
    </div>
  )
}
